import { useState, useCallback } from 'react';
import { chemoTranslations } from './chemoTranslations';
import {
    SERVER_VALUE_YES,
    SERVER_VALUE_NO,
    EMPTY_STRING,
    HTTP_METHOD_PATCH,
    CHEMO_RECORDS_ENDPOINT,
    ERROR_SPLIT_DELIMITER,
    FIELD_NAMES
} from './chemoConstants';

export const useChemoRecordsEdit = ({
    fetchedRecords,
    setFetchedRecords,
    originalRecords,
    KLINE,
    execute,
    fetchReports,
    setLoading,
    setError
}) => {
    const [editedRecords, setEditedRecords] = useState([]);

    const updateEditedRecords = useCallback((records) => {
        setEditedRecords(records);
        setFetchedRecords(records);
    }, [setFetchedRecords]);

    const STATUS_FIELDS = [FIELD_NAMES.APPROVED, FIELD_NAMES.NOT_APPROVED, FIELD_NAMES.FINISH];

    const handleCheckboxChange = useCallback((index, field) => {
        setFetchedRecords(prev => {
            const newRecords = [...prev];
            const newValue = !newRecords[index][field];

            const updatedRecord = { ...newRecords[index], [field]: newValue };

            if (newValue && STATUS_FIELDS.includes(field)) {
                STATUS_FIELDS.forEach(f => {
                    if (f !== field) updatedRecord[f] = false;
                });
            }

            newRecords[index] = updatedRecord;
            setEditedRecords(newRecords);
            return newRecords;
        });
    }, [setFetchedRecords]);

    const handleFieldChange = useCallback((index, field, value) => {
        setFetchedRecords(prev => {
            const newRecords = [...prev];
            newRecords[index] = {
                ...newRecords[index],
                [field]: value
            };

            setEditedRecords(newRecords);

            return newRecords;
        });
    }, [setFetchedRecords]);

    const saveChemoReport = useCallback(async (reportData) => {
        setLoading(true);
        setError(null);

        try {
            const convertToServerFormat = (value) => {
                if (value === true || value === SERVER_VALUE_YES) return SERVER_VALUE_YES;
                if (value === false || value === SERVER_VALUE_NO || value === null || value === undefined) return null;
                return value;
            };

            const recordsToSave = editedRecords
                .map((record, index) => {
                    const originalRecord = originalRecords[index];
                    if (!originalRecord) return null;

                    const filteredRecord = {
                        rowId: record[FIELD_NAMES.ROW_ID]
                    };

                    let hasChanges = false;

                    const originalApproved = convertToServerFormat(originalRecord[FIELD_NAMES.APPROVED]);
                    const currentApproved = convertToServerFormat(record[FIELD_NAMES.APPROVED]);
                    if (currentApproved === SERVER_VALUE_YES && originalApproved !== SERVER_VALUE_YES) {
                        filteredRecord[FIELD_NAMES.APPROVED] = SERVER_VALUE_YES;
                        hasChanges = true;
                    }

                    const originalNotApproved = convertToServerFormat(originalRecord[FIELD_NAMES.NOT_APPROVED]);
                    const currentNotApproved = convertToServerFormat(record[FIELD_NAMES.NOT_APPROVED]);
                    if (currentNotApproved === SERVER_VALUE_YES && originalNotApproved !== SERVER_VALUE_YES) {
                        filteredRecord[FIELD_NAMES.NOT_APPROVED] = SERVER_VALUE_YES;
                        hasChanges = true;
                    }

                    const originalFinish = convertToServerFormat(originalRecord[FIELD_NAMES.FINISH]);
                    const currentFinish = convertToServerFormat(record[FIELD_NAMES.FINISH]);
                    if (currentFinish === SERVER_VALUE_YES && originalFinish !== SERVER_VALUE_YES) {
                        filteredRecord[FIELD_NAMES.FINISH] = SERVER_VALUE_YES;
                        hasChanges = true;
                    }

                    const originalToDueDate = originalRecord[FIELD_NAMES.TO_DUE_DATE] || EMPTY_STRING;
                    const currentToDueDate = record[FIELD_NAMES.TO_DUE_DATE] || EMPTY_STRING;
                    if (currentToDueDate.trim() !== EMPTY_STRING && currentToDueDate !== originalToDueDate) {
                        filteredRecord[FIELD_NAMES.TO_DUE_DATE] = currentToDueDate;
                        hasChanges = true;
                    }

                    return hasChanges ? filteredRecord : null;
                })
                .filter(record => record !== null);

            const payload = {
                [FIELD_NAMES.KLINE]: KLINE,
                records: recordsToSave
            };

            const response = await execute(
                HTTP_METHOD_PATCH,
                `${process.env.REACT_APP_BASEURL}${CHEMO_RECORDS_ENDPOINT}`,
                payload
            );

            await fetchReports();
            return {
                success: true,
                data: response,
                approvalWarning: response?.approvalWarning || null
            };
        } catch (err) {
            const firstError = err.data?.errors?.[0];
            const detailPrefix = firstError?.details?.split(ERROR_SPLIT_DELIMITER)?.[0];
            const rowId = firstError?.rowId;

            const errorMessage = detailPrefix
                ? `${detailPrefix}${chemoTranslations.errors.patientError}${rowId ?? ''}`
                : chemoTranslations.errors.saveError;
            setError(errorMessage);
            return { success: false, error: err.message };
        } finally {
            setLoading(false);
        }
    }, [execute, editedRecords, originalRecords, KLINE, fetchReports, setLoading, setError]);

    const resetEditedRecords = useCallback(() => {
        setEditedRecords([]);
    }, []);

    const syncEditedRecords = useCallback((records) => {
        setEditedRecords(records);
    }, []);

    return {
        editedRecords,
        setEditedRecords,
        updateEditedRecords,
        handleCheckboxChange,
        handleFieldChange,
        saveChemoReport,
        resetEditedRecords,
        syncEditedRecords
    };
};
